import { useEffect, useRef, useState } from "react";
import { useAuthContext } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { Banner, SectionHeading } from "../settings/settingsUi";
import { agentMeApi } from "@/api/agentMeApi";

function getInitials(name, email) {
  if (name && name.trim()) {
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join("");
  }
  return (email || "?")[0].toUpperCase();
}

export default function PersonalProfileSection({
  displayName,
  onDisplayNameChange,
  onSave,
  saving,
  loading,
  banner,
  onDismissBanner,
}) {
  const { user } = useAuthContext();
  const { activeWorkspaceId } = useWorkspace();
  const fileRef = useRef(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [slaBreachCount, setSlaBreachCount] = useState(null);

  useEffect(() => {
    let live = true;
    void agentMeApi
      .fetchMe({ workspaceId: activeWorkspaceId })
      .then((payload) => {
        if (!live) return;
        setSlaBreachCount(Number(payload?.sla_breach_count) || 0);
      })
      .catch(() => {
        if (live) setSlaBreachCount(null);
      });
    return () => {
      live = false;
    };
  }, [activeWorkspaceId]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target?.result);
    reader.readAsDataURL(file);
  };

  const initials = getInitials(displayName, user?.email);

  if (loading) {
    return <p className="text-sm text-slate-500">Loading profile…</p>;
  }

  return (
    <div className="space-y-5">
      <SectionHeading
        label="Profile"
        desc="Your display name is saved for this workspace and visible to teammates."
      />

      <div className="flex items-center gap-5">
        <div className="relative">
          {avatarPreview ? (
            <img
              src={avatarPreview}
              alt="Avatar preview"
              className="h-16 w-16 rounded-2xl object-cover ring-2 ring-white shadow-md"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-teal-700 text-xl font-bold text-white shadow-md ring-2 ring-white">
              {initials}
            </div>
          )}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow ring-1 ring-slate-200 hover:bg-slate-50 transition-colors"
            title="Change photo (preview only)"
          >
            <i className="ri-camera-line text-[11px] text-slate-600" aria-hidden />
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {displayName || user?.email || "—"}
          </p>
          <p className="text-xs text-slate-500">{user?.email}</p>
          {slaBreachCount != null ? (
            <p className="mt-1 text-xs font-semibold text-rose-700 dark:text-rose-300">
              SLA breaches: {slaBreachCount}
            </p>
          ) : null}
          {avatarPreview && (
            <button
              type="button"
              onClick={() => setAvatarPreview(null)}
              className="mt-1 text-[11px] text-rose-600 hover:underline"
            >
              Remove preview
            </button>
          )}
        </div>
      </div>

      {banner && <Banner type={banner.type} text={banner.text} onDismiss={onDismissBanner} />}

      <div className="space-y-4">
        <div>
          <label htmlFor="display-name" className="mb-1.5 block text-sm font-semibold text-slate-800 dark:text-slate-200">
            Display name
          </label>
          <input
            id="display-name"
            className="ui-field"
            placeholder="e.g. Roshaan Ahmed"
            value={displayName}
            onChange={(e) => onDisplayNameChange(e.target.value)}
            disabled={saving}
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-semibold text-slate-800 dark:text-slate-200">
            Email address
          </label>
          <div className="relative">
            <input
              className="ui-field pr-28 opacity-70"
              value={user?.email ?? ""}
              readOnly
              tabIndex={-1}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-600">
              From login
            </span>
          </div>
          <p className="mt-1.5 text-[11px] text-slate-400">
            Email changes require re-authentication and are handled by your identity provider.
          </p>
        </div>
      </div>

      <button
        type="button"
        className="ui-btn-primary"
        onClick={() => void onSave()}
        disabled={saving || !displayName.trim()}
      >
        {saving ? "Saving…" : "Save profile"}
      </button>
    </div>
  );
}
